import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Calendar,
  ChevronRight,
  Edit,
  ListNumber,
  Plus,
  Save,
  Trash,
  X,
} from "@mynaui/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { IMatchDetailDTO } from "../../models/IMatch";
import moment from "moment";
import {
  getMatchById,
  updateAmericanoMatch,
  updateKnockoutMatch,
  updateMatch,
  updateMexicanoMatch,
} from "../../api/tournaments";
import { formatSideName, isAmericanoFormat, isLiveRoundFormat, otherSideScore } from "../../utils/methods";
import MatchEditDialog from "../../components/MatchEditDialog";
import DeleteMatchDialog from "../../components/DeleteMatchDialog";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { Alert, Button, Card, EmptyState, IconButton, Input, Loader, TableWrap, Tabs, Tag } from "../../ui";
import "./styles.css";

const TABS = [
  { label: "Games", value: "games" },
  { label: "Team 1 Players", value: "team1" },
  { label: "Team 2 Players", value: "team2" },
];

const MatchDetail = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("games");
  const [match, setMatch] = useState<IMatchDetailDTO>();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<"time" | "venue" | "score" | "best" | "all" | "schedule">("schedule");
  const [scoreError, setScoreError] = useState<string | null>(null);

  const getMatchDetail = async () => {
    setLoading(true);
    try {
      const res = await getMatchById(matchId!);
      setMatch(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const addGameHandler = () => {
    setMatch((prev: IMatchDetailDTO | undefined): IMatchDetailDTO => {
      const prevGameScores = prev?.gameScores ?? [];
      const newGameNumber = prevGameScores.length + 1;

      return {
        ...(prev ?? {
          id: "",
          homeTeamId: "",
          homeTeamName: "",
          awayTeamId: "",
          awayTeamName: "",
          round: "",
          bracket: "",
          venue: "",
          startTime: "",
          homeTeamSets: 0,
          awayTeamSets: 0,
          bestOf: 3,
          gameScores: [],
          isCompleted: false,
        }),
        gameScores: [
          ...prevGameScores,
          {
            awayScore: 0,
            gameNumber: newGameNumber,
            homeScore: 0,
            add: true,
            edit: false,
          },
        ],
      };
    });
  };

  // Americano/Mexicano: exactly one game whose scores add up to pointsPerMatch
  // (ties are legal and complete the match). When the backend hasn't provided
  // pointsPerMatch, the sum check is skipped and the backend's 400 is surfaced instead.
  const validateAmericanoScore = (matchData?: IMatchDetailDTO): string | null => {
    if (!isAmericanoFormat(matchData?.format)) return null;
    const games = matchData?.gameScores ?? [];
    if (games.length === 0) return null; // empty scores = un-scoring, allowed
    if (games.length > 1) return "This format has exactly one game per match.";
    const home = Number(games[0].homeScore);
    const away = Number(games[0].awayScore);
    if (Number.isNaN(home) || Number.isNaN(away) || home < 0 || away < 0)
      return "Scores must be non-negative numbers.";
    if (matchData?.pointsPerMatch != null && home + away !== matchData.pointsPerMatch)
      return `Scores must add up to ${matchData.pointsPerMatch} points (currently ${home + away}).`;
    return null;
  };

  const saveGameHandler = async (gameNum: number) => {
    const validationError = validateAmericanoScore(match);
    if (validationError) {
      setScoreError(validationError);
      return;
    }
    setScoreError(null);

    setMatch((prev) => {
      const updatedGameScores = prev?.gameScores ?? [];

      const idx = updatedGameScores.findIndex((x) => x.gameNumber === gameNum);
      if (idx !== -1)
        updatedGameScores[idx] = {
          ...updatedGameScores[idx],
          edit: false,
          add: false,
        };

      return { ...(prev as IMatchDetailDTO), gameScores: updatedGameScores };
    });

    await editMatchHandler();
  };

  const editGameHandler = (gameNum: number) => {
    setMatch((prev) => {
      const updatedGameScores = prev?.gameScores ?? [];

      const idx = updatedGameScores.findIndex((x) => x.gameNumber === gameNum);
      if (idx !== -1) updatedGameScores[idx] = { ...updatedGameScores[idx], edit: true };

      return { ...(prev as IMatchDetailDTO), gameScores: updatedGameScores };
    });
  };

  const cancelGameEdit = (gameNum: number) => {
    setMatch((prev) => {
      const updatedGameScores = prev?.gameScores ?? [];

      const idx = updatedGameScores.findIndex((x) => x.gameNumber === gameNum);
      if (idx !== -1) updatedGameScores[idx] = { ...updatedGameScores[idx], edit: false };

      return { ...(prev as IMatchDetailDTO), gameScores: updatedGameScores };
    });
  };

  const removeGameHandler = async (gameNum: number) => {
    let updated: IMatchDetailDTO | undefined;

    setMatch((prev) => {
      const updatedGameScores = (prev?.gameScores ?? []).filter((x) => x.gameNumber !== gameNum);
      updated = { ...(prev as IMatchDetailDTO), gameScores: updatedGameScores };
      return updated;
    });

    if (updated) {
      await editMatchHandler(updated);
    }
  };

  const editMatchHandler = async (updatedMatch?: IMatchDetailDTO) => {
    const matchData = updatedMatch ?? match;

    const requestBody = {
      gameScores: matchData?.gameScores ?? [],
      startTime: matchData?.startTime,
      venue: matchData?.venue,
    };

    try {
      if (matchData?.format === "Knockout") {
        await updateKnockoutMatch(matchId!, requestBody);
      } else if (matchData?.format === "Americano") {
        await updateAmericanoMatch(matchId!, requestBody);
      } else if (isLiveRoundFormat(matchData?.format)) {
        // Mexicano and Roulette share the api/Mexicano routes.
        await updateMexicanoMatch(matchId!, requestBody);
      } else {
        await updateMatch(matchId!, requestBody);
      }
      getMatchDetail();
    } catch (error: any) {
      console.error("error updating match", error.message);
      dispatch(setGeneralAlertMessage({
        message: error?.response?.data?.message || error?.message || "Failed to update match. Please try again.",
        type: "error"
      }));
    }
  };

  const matchScoreInputHandler = (gameNum: number, field: "homeScore" | "awayScore", value: string) => {
    setMatch((prev) => {
      const updatedGameScores = prev?.gameScores ?? [];

      // Americano/Mexicano: typing one score fills in the other.
      const otherScore = otherSideScore(prev?.format, prev?.pointsPerMatch, value);
      const other = otherScore === null ? {} : { [field === "homeScore" ? "awayScore" : "homeScore"]: otherScore };

      const idx = updatedGameScores.findIndex((x) => x.gameNumber === gameNum);
      if (idx !== -1)
        updatedGameScores[idx] = {
          ...updatedGameScores[idx],
          [field]: value,
          ...other,
        };

      return { ...(prev as IMatchDetailDTO), gameScores: updatedGameScores };
    });
  };

  useEffect(() => {
    getMatchDetail();
  }, []);

  const isPhone = window.screen.width < 768;
  const homeName = formatSideName(match?.homeTeamName, match?.homeTeam2Name, match?.homeExtraUnits);
  const awayName = formatSideName(match?.awayTeamName, match?.awayTeam2Name, match?.awayExtraUnits);
  // Before the result is in, both sides read as equals; after it, the winner stands out.
  const sideClass = (teamId?: string | null) =>
    match?.isCompleted ? (match?.winnerTeamId === teamId ? "bold" : "") : "bold";

  return (
    <div className="match-detail-page">
      <Button
        variant="ghost"
        size="sm"
        icon={<ArrowLeft />}
        className="match-detail-page__back"
        onClick={() => navigate(-1)}
      >
        Back
      </Button>

      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="match-detail-container">
            <Card className="match-card-container">
              <div className="round-venue-container">
                <p className="round">{match?.round}</p>
                <p className="venue">{match?.venue ?? "No venue specified"}</p>
              </div>
              <div className="teams-container">
                <p className={`team ${sideClass(match?.homeTeamId)}`}>{homeName}</p>
                <div className="score-container">
                  <p className={`score ${sideClass(match?.homeTeamId)}`}>{match?.isCompleted ? match?.homeTeamSets : ""}</p>
                  <p className="versus">-</p>
                  <p className={`score ${sideClass(match?.awayTeamId)}`}>{match?.isCompleted ? match?.awayTeamSets : ""}</p>
                </div>
                <p className={`team ${sideClass(match?.awayTeamId)}`}>{awayName}</p>
              </div>
              <div className="datetime-container">
                <p className="date">
                  {match?.startTime ? moment(match?.startTime).format("DD/MM/YYYY - hh:mm A") : "TBA"}
                </p>
                <Tag tone="neutral">Best of {match?.bestOf}</Tag>
              </div>
            </Card>
            <div className="match-actions-container">
              {isPhone ? (
                <Button
                  variant="tint"
                  block
                  icon={<Edit />}
                  onClick={() => {
                    setSelectedMode("all");
                    setIsEditOpen(true);
                  }}
                >
                  Edit match
                </Button>
              ) : (
                <>
                  <Button
                    variant="tint"
                    block
                    icon={<Calendar />}
                    onClick={() => {
                      setSelectedMode("schedule");
                      setIsEditOpen(true);
                    }}
                  >
                    Change date, time & venue
                  </Button>

                  {!isAmericanoFormat(match?.format) && (
                    <Button
                      variant="tint"
                      block
                      icon={<ListNumber />}
                      onClick={() => {
                        setSelectedMode("best");
                        setIsEditOpen(true);
                      }}
                    >
                      Change best of
                    </Button>
                  )}
                </>
              )}
              {!isAmericanoFormat(match?.format) && (
                <Button variant="danger-tint" block icon={<Trash />} onClick={() => setIsDeleteDialogOpen(true)}>
                  Delete match
                </Button>
              )}
            </div>
          </div>

          <Tabs
            className="match-detail-page__tabs"
            ariaLabel="Match sections"
            value={activeTab}
            onChange={setActiveTab}
            options={TABS.map((tab) => ({
              value: tab.value,
              label: tab.value === "team1" ? homeName || "Team 1" : tab.value === "team2" ? awayName || "Team 2" : tab.label,
            }))}
          />

          <div className="tab-content">
            {activeTab === "games" ? (
              <div className="games-container">
                {isAmericanoFormat(match?.format) && (
                  <Alert tone="info">
                    One game per match
                    {match?.pointsPerMatch != null
                      ? ` — scores must add up to ${match.pointsPerMatch} points`
                      : ""}
                    . Ties are allowed.
                  </Alert>
                )}
                {scoreError && <Alert tone="danger">{scoreError}</Alert>}
                {match?.gameScores && match?.gameScores.length > 0 ? (
                  <>
                    {match?.gameScores
                      .sort((a, b) => (a.gameNumber > b.gameNumber ? -1 : 1))
                      .map((game, id) => (
                        <div className="game-container" key={id}>
                          <p className="game-num">Game {game.gameNumber}</p>
                          <div className="score-container">
                            <p className="team">{homeName}</p>
                            {game.add || game.edit ? (
                              <Input
                                inputSize="sm"
                                block={false}
                                aria-label={`${homeName} score`}
                                placeholder="Score"
                                className="score-input"
                                value={game.homeScore}
                                onChange={(e) => matchScoreInputHandler(game.gameNumber, "homeScore", e.target.value)}
                                onFocus={(e) => e.currentTarget.select()}
                              />
                            ) : (
                              <p className={`score-value ${game.homeScore > game.awayScore ? "bold" : ""}`}>
                                {game.homeScore}
                              </p>
                            )}
                            {!game.add && !game.edit && <p className="versus-txt">-</p>}
                            {game.add || game.edit ? (
                              <Input
                                inputSize="sm"
                                block={false}
                                aria-label={`${awayName} score`}
                                placeholder="Score"
                                className="score-input"
                                value={game.awayScore}
                                onChange={(e) => matchScoreInputHandler(game.gameNumber, "awayScore", e.target.value)}
                                onFocus={(e) => e.currentTarget.select()}
                              />
                            ) : (
                              <p className={`score-value ${game.homeScore < game.awayScore ? "bold" : ""}`}>
                                {game.awayScore}
                              </p>
                            )}
                            <p className="team">{awayName}</p>
                          </div>
                          <div className="actions">
                            {game.add || game.edit ? (
                              <>
                                <Button size="sm" icon={<Save />} onClick={() => saveGameHandler(game.gameNumber)}>
                                  Save
                                </Button>
                                {isPhone ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={<X />}
                                    onClick={() =>
                                      game.add ? removeGameHandler(game.gameNumber) : cancelGameEdit(game.gameNumber)
                                    }
                                  >
                                    Cancel
                                  </Button>
                                ) : (
                                  <IconButton
                                    label="Cancel"
                                    icon={<X size={18} />}
                                    onClick={() =>
                                      game.add ? removeGameHandler(game.gameNumber) : cancelGameEdit(game.gameNumber)
                                    }
                                  />
                                )}
                              </>
                            ) : (
                              <>
                                <Button variant="ghost" size="sm" icon={<Edit />} onClick={() => editGameHandler(game.gameNumber)}>
                                  Edit
                                </Button>

                                {isPhone ? (
                                  <Button
                                    variant="danger-tint"
                                    size="sm"
                                    icon={<Trash />}
                                    onClick={() => removeGameHandler(game.gameNumber)}
                                  >
                                    Delete
                                  </Button>
                                ) : (
                                  <IconButton
                                    label="Delete"
                                    variant="danger"
                                    icon={<Trash size={18} />}
                                    onClick={() => removeGameHandler(game.gameNumber)}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    {match.gameScores.length < (isAmericanoFormat(match?.format) ? 1 : match.bestOf) && (
                      <div className="add-game-button-container">
                        <Button variant="tint" icon={<Plus />} onClick={addGameHandler}>
                          Add game
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <Card padding="none">
                    <EmptyState
                      title="No games recorded for this match"
                      action={
                        <Button icon={<Plus />} onClick={addGameHandler}>
                          Add game
                        </Button>
                      }
                    />
                  </Card>
                )}
              </div>
            ) : (
              <TableWrap>
                <table className="ui-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Nationality</th>
                      <th>Notes</th>
                      <th className="ui-table__actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {(activeTab === "team1" ? match?.homeTeamPlayers : match?.awayTeamPlayers)?.map((player, id) => (
                      <tr key={id} className="is-clickable" onClick={() => navigate(`/dashboard/players/${player.playerId}`)}>
                        <td className="ui-table__primary">
                          {player.firstName} {player.lastName ?? ""}
                        </td>
                        <td>{player.nationality}</td>
                        <td>{player.notes ?? "-"}</td>
                        <td className="ui-table__actions match-detail-page__chevron">
                          <ChevronRight size={16} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            )}
          </div>
        </>
      )}

      <MatchEditDialog
        match={match!}
        isOpen={isEditOpen}
        mode={selectedMode}
        onClose={(update) => {
          if (update) getMatchDetail();
          setIsEditOpen(false);
        }}
      />

      <DeleteMatchDialog
        isOpen={isDeleteDialogOpen}
        matchId={match?.id!}
        matchFormat={match?.format!}
        formatId={match?.formatId!}
        onClose={(update) => {
          setIsDeleteDialogOpen(false);
          if (update) navigate(-1);
        }}
      />
    </div>
  );
};

export default MatchDetail;
